import { type FormEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import { ApiError, login, loginWithPasskey, passkeySupported } from '../lib/api.js';
import { BrandLogo } from './BrandLogo.js';

interface Props {
  /**
   * Called with no arguments after a successful POST /auth/login. The
   * cookie is already set by the server; the caller is expected to
   * refetch session/app state.
   */
  onSuccess: () => void;
  /** Test seam: replace global fetch in tests. */
  fetchImpl?: typeof fetch;
}

/**
 * Compact, desktop-dense login screen. Shown when the dashboard runs in
 * cookie/hybrid mode and GET /auth/me returns 401. The password is held
 * only in component state (not in localStorage / sessionStorage) and is
 * cleared from React state after the fetch resolves either way.
 */
export function LoginScreen({ onSuccess, fetchImpl }: Props) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canUsePasskey = passkeySupported();

  async function onPasskey() {
    if (submitting || passkeyBusy) return;
    setError(null);
    setPasskeyBusy(true);
    try {
      await loginWithPasskey(fetchImpl ? { fetchImpl } : {});
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 503) setError('Passkeys are not enabled on the server.');
        else if (err.kind === 'unauthorized') setError('No matching passkey for this device.');
        else if (err.kind === 'rate_limited') setError('Too many attempts — try again later.');
        else if (err.kind === 'server') setError('Server error — try again.');
        else setError('Passkey sign-in failed.');
      } else if (err instanceof DOMException && err.name === 'NotAllowedError') {
        // User dismissed the OS prompt, or it timed out. Not an error worth shouting about.
        setError(null);
      } else {
        setError('Passkey sign-in failed.');
      }
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    const attempt = password;
    try {
      await login(attempt, fetchImpl ? { fetchImpl } : {});
      // Drop the password from state immediately on success — it's done
      // its job and there's no reason to keep it around. flushSync
      // forces the clear to commit BEFORE `onSuccess()` flips the
      // session state (which would unmount this component). Without
      // it, React batches both setStates and the unmount can race the
      // clear, leaving a stale value in the detached input element.
      // The test for "password cleared from state" depends on this.
      flushSync(() => setPassword(''));
      onSuccess();
    } catch (err) {
      // Wipe the field on a bad password so the operator retypes; this
      // also makes the "no password lingers in state" property easy to
      // assert in tests.
      setPassword('');
      if (err instanceof ApiError) {
        if (err.kind === 'unauthorized') setError('Wrong password.');
        else if (err.kind === 'server') setError('Server error — try again.');
        else if (err.status === 503) setError('Session auth is not enabled on the server.');
        else setError('Login failed.');
      } else {
        setError('Login failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="login-screen"
      className="flex h-screen w-screen items-center justify-center bg-zinc-950 text-zinc-100"
    >
      <form
        onSubmit={onSubmit}
        className="flex w-80 flex-col gap-5 rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-6 shadow-2xl shadow-black/40 backdrop-blur"
      >
        <div className="flex flex-col items-start gap-3">
          <BrandLogo tone="light" className="h-10 w-auto" />
          <div className="flex flex-col gap-1">
            <div className="text-[15px] font-semibold tracking-tight text-zinc-100">
              Sign in to Communication
            </div>
            <div className="text-[12px] text-zinc-500">
              Operator password. A session cookie is set on success.
            </div>
          </div>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium text-zinc-400">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="password"
            name="password"
            className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-[13px] text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-zinc-600"
            disabled={submitting}
          />
        </label>

        <button
          type="submit"
          disabled={submitting || passkeyBusy || password.length === 0}
          className="inline-flex items-center justify-center rounded-md px-3 py-2 text-[13px] font-medium text-white shadow-sm transition-colors disabled:opacity-40"
          style={{
            backgroundColor: 'var(--accent-strong, var(--accent))',
          }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        {canUsePasskey && (
          <>
            <div className="flex items-center gap-3 text-[11px] text-zinc-600">
              <span className="h-px flex-1 bg-zinc-800" />
              or
              <span className="h-px flex-1 bg-zinc-800" />
            </div>
            <button
              type="button"
              onClick={onPasskey}
              disabled={submitting || passkeyBusy}
              aria-label="sign in with passkey"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-[13px] font-medium text-zinc-100 transition-colors hover:border-zinc-600 disabled:opacity-40"
            >
              {passkeyBusy ? 'Waiting for passkey…' : 'Sign in with passkey'}
            </button>
          </>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-[12px] text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
